import type {
  ReportAction,
  ReportActionRequest,
  ReportActionStatus,
  ReportAdviceRequest,
  ReportAmenityLifts,
  ReportBoard,
  ReportBoardRef,
  ReportBoardRequest,
  ReportBoardsResponse,
  ReportCatalog,
  ReportDayReplayRequest,
  ReportDayReplayResponse,
  ReportDigest,
  ReportDigestPreview,
  ReportDigestRequest,
  ReportFloorPlan,
  ReportFloorPlanRequest,
  ReportInsightsRequest,
  ReportInsightsResponse,
  ReportLiveMapResponse,
  ReportMapRequest,
  ReportMapResponse,
  ReportOutlook,
  ReportOutlookRequest,
  ReportQuery,
  ReportQueryResult,
  ReportRecordsPage,
  ReportRecordsRequest,
  ReportRightSizing,
  ReportRollupResponse,
  ReportStackPlan,
  ReportStatusFlow,
  ReportTeamDays,
  ReportTile
} from "../types/reporting";
import { BaseSettingsEndpoints } from "./base";

const REPORTS_BASE_PATH = "/v1/reports";

export class ReportingClient extends BaseSettingsEndpoints {
  getCatalog() {
    return this.get<ReportCatalog>(`${REPORTS_BASE_PATH}/catalog`);
  }

  query(body: ReportQuery) {
    return this.post<ReportQuery, ReportQueryResult>(
      `${REPORTS_BASE_PATH}/query`,
      body
    );
  }

  /**
   * The catalog query behind a plain-language question, and the second
   * measure to draw beside it when the question set two against each other
   * ("open requests vs closed"); nothing is run.
   */
  ask(question: string) {
    return this.post<
      { question: string },
      { query: ReportQuery; with?: string }
    >(`${REPORTS_BASE_PATH}/ask`, { question });
  }

  records(body: ReportRecordsRequest) {
    return this.post<ReportRecordsRequest, ReportRecordsPage>(
      `${REPORTS_BASE_PATH}/records`,
      body
    );
  }

  /** The raw CSV response; the caller turns it into a download. */
  exportRecords(body: ReportRecordsRequest) {
    return this.requestStream(
      "POST",
      `${REPORTS_BASE_PATH}/records/export`,
      body
    );
  }

  /** Per-resource values on one floor, for drawing a heatmap. */
  map(body: ReportMapRequest) {
    return this.post<ReportMapRequest, ReportMapResponse>(
      `${REPORTS_BASE_PATH}/map`,
      body
    );
  }

  /** A floor right now: free, expected, present per resource. */
  liveMap(floorId: string) {
    return this.post<{ floor_id: string }, ReportLiveMapResponse>(
      `${REPORTS_BASE_PATH}/map/live`,
      { floor_id: floorId }
    );
  }

  /** A floor on one day: every reservation with its hours, for replaying the day. */
  dayReplay(body: ReportDayReplayRequest) {
    return this.post<ReportDayReplayRequest, ReportDayReplayResponse>(
      `${REPORTS_BASE_PATH}/map/day`,
      body
    );
  }

  /** What changed on a board, biggest move first. */
  insights(body: ReportInsightsRequest) {
    return this.post<ReportInsightsRequest, ReportInsightsResponse>(
      `${REPORTS_BASE_PATH}/insights`,
      body
    );
  }

  /** The coming working days, forecast from what is booked and how days usually fill. */
  outlook(body: ReportOutlookRequest) {
    return this.post<ReportOutlookRequest, ReportOutlook>(
      `${REPORTS_BASE_PATH}/outlook`,
      body
    );
  }

  /** What each floor could release, and dedicated desks that sit idle. */
  rightSizing(body: ReportAdviceRequest) {
    return this.post<ReportAdviceRequest, ReportRightSizing>(
      `${REPORTS_BASE_PATH}/advice/right-sizing`,
      body
    );
  }

  /** Where requests wait and where they go next. */
  statusFlow(body: ReportAdviceRequest) {
    return this.post<ReportAdviceRequest, ReportStatusFlow>(
      `${REPORTS_BASE_PATH}/advice/status-flow`,
      body
    );
  }

  /** Which weekdays each team comes in. */
  teamDays(body: ReportAdviceRequest) {
    return this.post<ReportAdviceRequest, ReportTeamDays>(
      `${REPORTS_BASE_PATH}/advice/team-days`,
      body
    );
  }

  /** Floors as bars split by team. */
  stackPlan(body: ReportAdviceRequest) {
    return this.post<ReportAdviceRequest, ReportStackPlan>(
      `${REPORTS_BASE_PATH}/advice/stack-plan`,
      body
    );
  }

  /** One floor's desks, who they belong to, and which to release first. */
  floorPlan(body: ReportFloorPlanRequest) {
    return this.post<ReportFloorPlanRequest, ReportFloorPlan>(
      `${REPORTS_BASE_PATH}/advice/floor-plan`,
      body
    );
  }

  /** Does an amenity make a desk get booked more? */
  amenityLift(body: ReportAdviceRequest) {
    return this.post<ReportAdviceRequest, ReportAmenityLifts>(
      `${REPORTS_BASE_PATH}/advice/amenity-lift`,
      body
    );
  }

  rollupState() {
    return this.get<ReportRollupResponse>(`${REPORTS_BASE_PATH}/rollup`);
  }

  rebuildRollup() {
    return this.post<Record<string, never>, ReportRollupResponse>(
      `${REPORTS_BASE_PATH}/rollup/rebuild`,
      {}
    );
  }

  listActions(status?: ReportActionStatus) {
    return this.get<{ actions: ReportAction[] }>(
      `${REPORTS_BASE_PATH}/actions${status ? `?status=${status}` : ""}`
    );
  }

  createAction(body: ReportActionRequest) {
    return this.post<ReportActionRequest, ReportAction>(
      `${REPORTS_BASE_PATH}/actions`,
      body
    );
  }

  updateActionStatus(id: string, status: ReportActionStatus) {
    return this.requestWithStatus<ReportAction>(
      "PATCH",
      `${REPORTS_BASE_PATH}/actions/${encodeURIComponent(id)}`,
      { status }
    );
  }

  listDigests() {
    return this.get<{ digests: ReportDigest[] }>(
      `${REPORTS_BASE_PATH}/digests`
    );
  }

  saveDigest(body: ReportDigestRequest) {
    return this.post<ReportDigestRequest, ReportDigest>(
      `${REPORTS_BASE_PATH}/digests`,
      body
    );
  }

  deleteDigest(id: string) {
    return this.delete<null>(`${REPORTS_BASE_PATH}/digests/${id}`);
  }

  /** The weekly email for a board as the caller would get it today; nothing is sent. */
  previewDigest(body: ReportBoardRef) {
    return this.post<ReportBoardRef, ReportDigestPreview>(
      `${REPORTS_BASE_PATH}/digests/preview`,
      body
    );
  }

  /**
   * A private board built from a plain-language request: the standard
   * questions of the topics it names, scoped to the place and period it
   * names, with the empty ones left out.
   */
  composeBoard(question: string) {
    return this.post<{ question: string }, ReportBoard>(
      `${REPORTS_BASE_PATH}/boards/compose`,
      { question }
    );
  }

  listBoards() {
    return this.get<ReportBoardsResponse>(`${REPORTS_BASE_PATH}/boards`);
  }

  createBoard(body: ReportBoardRequest) {
    return this.post<ReportBoardRequest, ReportBoard>(
      `${REPORTS_BASE_PATH}/boards`,
      body
    );
  }

  updateBoard(id: string, body: ReportBoardRequest) {
    return this.update<ReportBoardRequest, ReportBoard>(
      `${REPORTS_BASE_PATH}/boards/${id}`,
      body
    );
  }

  updateBoardTiles(id: string, tiles: ReportTile[]) {
    return this.update<{ tiles: ReportTile[] }, ReportBoard>(
      `${REPORTS_BASE_PATH}/boards/${id}/tiles`,
      { tiles }
    );
  }

  deleteBoard(id: string) {
    return this.delete<null>(`${REPORTS_BASE_PATH}/boards/${id}`);
  }

  duplicateDefaultBoard(key: string) {
    return this.post<Record<string, never>, ReportBoard>(
      `${REPORTS_BASE_PATH}/boards/defaults/${key}/duplicate`,
      {}
    );
  }
}
